import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Chip from "@material-ui/core/Chip";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    extraAttr: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        margin: 2,
    },
}));

const ScheduleSchema = Yup.object().shape({
    startTime: Yup.string().required("Required"),
    endTime: Yup.string().required("Required"),
    message: Yup.string().required("Required"),
});

const ScheduleModal = ({ open, onClose, scheduleId }) => {
    const classes = useStyles();
    const { findAll: findAllQueues } = useQueues();
    const [queues, setQueues] = useState([]);
    const [selectedQueues, setSelectedQueues] = useState([]);
    const [selectedDays, setSelectedDays] = useState([]);

    const initialState = {
        startTime: "",
        endTime: "",
        message: "",
    };

    const [schedule, setSchedule] = useState(initialState);

    const daysOptions = [
        { id: 'Monday', label: 'Segunda-feira' },
        { id: 'Tuesday', label: 'Terça-feira' },
        { id: 'Wednesday', label: 'Quarta-feira' },
        { id: 'Thursday', label: 'Quinta-feira' },
        { id: 'Friday', label: 'Sexta-feira' },
        { id: 'Saturday', label: 'Sábado' },
        { id: 'Sunday', label: 'Domingo' }
    ];

    useEffect(() => {
        const loadQueues = async () => {
            const list = await findAllQueues();
            setQueues(list);
        };
        loadQueues();
    }, []);

    useEffect(() => {
        const fetchSchedule = async () => {
            if (!scheduleId) {
                setSchedule(initialState);
                setSelectedDays([]);
                setSelectedQueues([]);
                return;
            }
            try {
                const { data } = await api.get(`/schedules/${scheduleId}`);
                setSchedule(data);
                setSelectedDays(data.daysOfWeek.split(','));
                setSelectedQueues(data.queues.map(q => q.id));
            } catch (err) {
                toastError(err);
            }
        };
        fetchSchedule();
    }, [scheduleId, open]);

    const handleClose = () => {
        onClose();
        setSchedule(initialState);
        setSelectedDays([]);
        setSelectedQueues([]);
    };

    const handleSaveSchedule = async (values) => {
        const scheduleData = {
            ...values,
            daysOfWeek: selectedDays.join(','),
            queues: selectedQueues
        };
        try {
            if (scheduleId) {
                await api.put(`/schedules/${scheduleId}`, scheduleData);
                toast.success("Escala editada com sucesso"); // Translation placeholder
            } else {
                await api.post("/schedules", scheduleData);
                toast.success("Escala criada com sucesso"); // Translation placeholder
            }
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {scheduleId ? `${i18n.t("schedules.titles.edit")}` : `${i18n.t("schedules.titles.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={schedule}
                    enableReinitialize={true}
                    validationSchema={ScheduleSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveSchedule(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values }) => (
                        <Form>
                            <DialogContent dividers>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Field
                                        as={TextField}
                                        label="Início"
                                        type="time"
                                        name="startTime"
                                        InputLabelProps={{ shrink: true }}
                                        error={touched.startTime && Boolean(errors.startTime)}
                                        helperText={touched.startTime && errors.startTime}
                                        variant="outlined"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label="Fim"
                                        type="time"
                                        name="endTime"
                                        InputLabelProps={{ shrink: true }}
                                        error={touched.endTime && Boolean(errors.endTime)}
                                        helperText={touched.endTime && errors.endTime}
                                        variant="outlined"
                                        fullWidth
                                    />
                                </div>

                                <FormControl variant="outlined" margin="dense" fullWidth style={{ marginTop: 20 }}>
                                    <InputLabel>Dias da Semana</InputLabel>
                                    <Select
                                        multiple
                                        value={selectedDays}
                                        onChange={(e) => setSelectedDays(e.target.value)}
                                        renderValue={(selected) => (
                                            <div className={classes.chips}>
                                                {selected.map((value) => (
                                                    <Chip key={value} label={daysOptions.find(d => d.id === value)?.label || value} className={classes.chip} />
                                                ))}
                                            </div>
                                        )}
                                    >
                                        {daysOptions.map((option) => (
                                            <MenuItem key={option.id} value={option.id}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl variant="outlined" margin="dense" fullWidth style={{ marginTop: 20 }}>
                                    <InputLabel>Setores</InputLabel>
                                    <Select
                                        multiple
                                        value={selectedQueues}
                                        onChange={(e) => setSelectedQueues(e.target.value)}
                                        renderValue={(selected) => (
                                            <div className={classes.chips}>
                                                {selected.map((value) => {
                                                    const q = queues.find(queue => queue.id === value);
                                                    return (
                                                        <Chip key={value} label={q ? q.name : value} className={classes.chip} style={{ backgroundColor: q?.color }} />
                                                    );
                                                })}
                                            </div>
                                        )}
                                        label="Setores"
                                    >
                                        {queues.map((queue) => (
                                            <MenuItem key={queue.id} value={queue.id}>
                                                {queue.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Field
                                    as={TextField}
                                    label="Mensagem de Ausência"
                                    name="message"
                                    error={touched.message && Boolean(errors.message)}
                                    helperText={touched.message && errors.message}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    style={{ marginTop: 20 }}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("schedules.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {scheduleId ? `${i18n.t("schedules.buttons.okEdit")}` : `${i18n.t("schedules.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default ScheduleModal;
